import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Receipt, Search, Filter, X, Save, Pencil, AlertTriangle, CheckCircle2,
  Ban, ArrowRight, ClipboardList, Mail,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const FILTERS = [
  { key: "pending", label: "Pending bill entry", icon: ClipboardList },
  { key: "mismatch", label: "Mismatch (bill ≠ sale)", icon: AlertTriangle },
  { key: "all", label: "All bills", icon: Receipt },
  { key: "not_required_txn", label: "Not required (per challan)", icon: Ban },
];

function BillEditModal({ row, onClose, onSaved }) {
  const [billNo, setBillNo] = useState(row.bill_no || "");
  const [billAmount, setBillAmount] = useState(row.bill_amount || "");
  const [busy, setBusy] = useState(false);
  const ba = Number(billAmount) || 0;
  const mismatch = ba > 0 && Math.abs(ba - row.sale_amount) > 0.5;
  const diff = ba - row.sale_amount;
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/transactions/${row.txn_id}/bill-no`, {
        bill_no: billNo,
        bill_amount: billAmount === "" ? 0 : Number(billAmount) || 0,
      });
      toast.success("Bill saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="pb-bill-modal">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="small-label text-gray-500">Bill for {row.party_name}</div>
            <div className="font-heading font-black text-lg">Challan {row.challan_no || <span className="text-gray-400 italic">no #</span>}</div>
            <div className="text-xs text-gray-500 mt-0.5">{row.date} · Sale ₹ {formatMoney(row.sale_amount)}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-sm"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="small-label block mb-1.5">Bill / Invoice number</label>
            <input className="input-lg" placeholder="e.g. INV-2026-0087" value={billNo}
              onChange={(e) => setBillNo(e.target.value)} autoFocus
              data-testid="pb-bill-no-input" />
          </div>
          <div>
            <label className="small-label block mb-1.5">Bill amount (₹)</label>
            <input type="number" step="0.01"
              className={`input-lg text-right data-num ${mismatch ? "border-[#B91C1C] bg-[#FEE2E2]" : ""}`}
              value={billAmount}
              onChange={(e) => setBillAmount(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && save()}
              data-testid="pb-bill-amount-input" />
            {mismatch ? (
              <div className="text-xs mt-1.5 flex items-center gap-1.5 text-[#B91C1C] font-semibold">
                <AlertTriangle size={12} /> Mismatch ₹ {formatMoney(Math.abs(diff))} <span className="text-gray-500 font-normal">({diff > 0 ? "bill higher" : "bill lower"})</span>
              </div>
            ) : ba > 0 ? (
              <div className="text-xs mt-1.5 text-[#15803D] flex items-center gap-1.5"><CheckCircle2 size={12} /> Matches sale amount</div>
            ) : (
              <div className="text-xs mt-1.5 text-gray-500">Leave 0 if bill amount equals sale amount.</div>
            )}
          </div>
        </div>
        <div className="p-5 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="h-11 px-5 border border-gray-300 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary h-11 px-6 flex items-center gap-2" data-testid="pb-bill-save">
            <Save size={16} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PendingBillEntry() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [filter, setFilter] = useState("pending");
  const [partyType, setPartyType] = useState("all");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editRow, setEditRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pending-bills", { params: { filter, party_type: partyType } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter, partyType]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data?.rows || [];
    return (data?.rows || []).filter((r) => {
      const name = (r.party_name || "").toLowerCase();
      const ch = (r.challan_no || "").toLowerCase();
      const bn = (r.bill_no || "").toLowerCase();
      const emails = Array.isArray(r.emails) && r.emails.length ? r.emails : (r.email ? [r.email] : []);
      const em = emails.join(" ").toLowerCase();
      return name.includes(q) || ch.includes(q) || bn.includes(q) || em.includes(q);
    });
  }, [data, search]);

  const toggleTxnFlag = async (row) => {
    const label = row.bill_not_required_txn ? "Enable bill entry for this challan?" : "Mark this challan as not needing a bill?";
    if (!window.confirm(label)) return;
    try {
      await api.patch(`/transactions/${row.txn_id}/bill-not-required`, { bill_not_required: !row.bill_not_required_txn });
      toast.success(row.bill_not_required_txn ? "Bill entry re-enabled" : "Marked not-required");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const summary = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="pending-bills-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Receipt size={20} className="text-black" />
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">Pending Bill Entry</h2>
        <p className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Challans waiting for bill number & amount
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="card-flat p-5 bg-[#FEF3C7] border-[#FDE68A]">
          <div className="small-label text-[#B45309]">Pending bill entry</div>
          <div className="data-num text-3xl mt-1 text-[#B45309]" data-testid="pb-pending-count">{summary.pending_count || 0}</div>
          <div className="text-xs text-[#B45309]/80 mt-1">Awaiting bill no. + amount</div>
        </div>
        <div className="card-flat p-5 bg-[#FEE2E2] border-[#FECACA]">
          <div className="small-label text-[#B91C1C] flex items-center gap-1.5"><AlertTriangle size={12} /> Mismatch</div>
          <div className="data-num text-3xl mt-1 text-[#B91C1C]" data-testid="pb-mismatch-count">{summary.mismatch_count || 0}</div>
          <div className="text-xs text-[#B91C1C]/80 mt-1">Bill ≠ sale amount</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500 flex items-center gap-1.5"><Ban size={12} /> Not needed</div>
          <div className="data-num text-3xl mt-1" data-testid="pb-nr-txn-count">{summary.not_required_txn_count || 0}</div>
          <div className="text-xs text-gray-500 mt-1">Opted-out challans</div>
        </div>
      </div>

      {/* Controls */}
      <div className="card-flat p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={14} className="text-gray-500" />
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`h-9 px-3 rounded-sm text-xs font-semibold border flex items-center gap-1.5 ${filter === f.key ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
              data-testid={`pb-filter-${f.key}`}
            >
              <f.icon size={12} />
              {f.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-3 flex-wrap">
          <select className="input-lg h-9 w-auto" value={partyType}
            onChange={(e) => setPartyType(e.target.value)}
            data-testid="pb-party-type">
            <option value="all">All parties</option>
            <option value="customer">Customers only</option>
            <option value="vendor">Vendors only</option>
          </select>
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="input-lg h-9 pl-8 w-56" placeholder="Search party / challan / bill…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="pb-search" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card-flat overflow-auto">
        <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Date</th>
                <th className="text-left">Party</th>
                <th className="text-left">Challan</th>
                <th className="num">Sale ₹</th>
                <th className="text-left">Bill No.</th>
                <th className="num">Bill ₹</th>
                <th className="text-left">Status</th>
                <th className="text-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="8" className="py-8 text-center text-sm text-gray-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan="8" className="py-8 text-center text-sm text-gray-400">
                  <CheckCircle2 size={26} className="inline-block text-[#15803D] mb-1" />
                  <div>Nothing to show for this filter.</div>
                </td></tr>
              )}
              {rows.map((r) => {
                const mismatch = r.bill_mismatch;
                const rowBg = r.bill_not_required_txn
                  ? "bg-gray-50 text-gray-500"
                  : mismatch
                    ? "bg-[#FEE2E2]/50"
                    : "";
                return (
                  <tr key={r.txn_id} className={rowBg} data-testid={`pb-row-${r.txn_id}`}>
                    <td className="data-num text-xs">{r.date}</td>
                    <td>
                      <Link to={`/parties/${r.party_id}`} className="font-semibold hover:underline text-sm">{r.party_name}</Link>
                      <div className="text-[10px] text-gray-500 capitalize">{r.party_type}{r.phone ? ` · ${r.phone}` : ""}</div>
                      {(() => {
                        const emails = Array.isArray(r.emails) && r.emails.length ? r.emails : (r.email ? [r.email] : []);
                        if (!emails.length) return null;
                        return (
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-gray-500 mt-0.5" data-testid={`pb-emails-${r.txn_id}`}>
                            <Mail size={10} className="text-gray-400 shrink-0" />
                            {emails.map((e, i) => (
                              <a key={i} href={`mailto:${e}?subject=${encodeURIComponent(`Bill for challan ${r.challan_no || ""}`)}`}
                                className="hover:text-black hover:underline break-all"
                                title={`Send bill to ${e}`}>{e}</a>
                            ))}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="data-num text-xs">{r.challan_no || <span className="text-gray-400 italic">no #</span>}</td>
                    <td className="num data-num text-xs">{formatMoney(r.sale_amount)}</td>
                    <td className="data-num text-sm">
                      {r.bill_no
                        ? <span className="font-semibold">{r.bill_no}</span>
                        : <span className="text-gray-400 italic text-xs">not added</span>}
                    </td>
                    <td className={`num data-num text-xs ${mismatch ? "text-[#B91C1C] font-bold" : ""}`}>
                      {r.bill_amount > 0 ? formatMoney(r.bill_amount) : <span className="text-gray-300">—</span>}
                    </td>
                    <td>
                      {r.bill_not_required_txn
                        ? <span className="inline-block px-2 py-0.5 text-xs rounded-sm bg-gray-200 text-gray-600 font-semibold">Not required</span>
                        : mismatch
                          ? <span className="inline-block px-2 py-0.5 text-xs rounded-sm bg-[#B91C1C] text-white font-semibold">Mismatch</span>
                          : r.has_bill
                            ? <span className="inline-block px-2 py-0.5 text-xs rounded-sm bg-[#DCFCE7] text-[#15803D] font-semibold">Done</span>
                            : <span className="inline-block px-2 py-0.5 text-xs rounded-sm bg-[#FEF3C7] text-[#B45309] font-semibold">Pending</span>}
                    </td>
                    <td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {!r.bill_not_required_txn && (
                          <button onClick={() => setEditRow(r)}
                            className="text-xs text-gray-500 hover:text-black inline-flex items-center gap-1 px-2 py-1 rounded-sm hover:bg-gray-100"
                            title={r.has_bill ? "Edit bill" : "Add bill"}
                            data-testid={`pb-edit-${r.txn_id}`}>
                            <Pencil size={12} />
                          </button>
                        )}
                        <button onClick={() => toggleTxnFlag(r)}
                          className={`text-xs px-2 py-1 rounded-sm flex items-center gap-1 border ${r.bill_not_required_txn ? "border-[#15803D] text-[#15803D] hover:bg-[#DCFCE7]" : "border-gray-300 text-gray-500 hover:text-black hover:border-black"}`}
                          title={r.bill_not_required_txn ? "Re-enable bill entry" : "Not required for this challan"}
                          data-testid={`pb-toggle-txn-${r.txn_id}`}>
                          {r.bill_not_required_txn ? <ArrowRight size={11} /> : <Ban size={11} />}
                          <span className="hidden sm:inline">{r.bill_not_required_txn ? "Enable" : "Skip"}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
      </div>

      {editRow && (
        <BillEditModal
          row={editRow}
          onClose={() => setEditRow(null)}
          onSaved={() => { setEditRow(null); load(); }}
        />
      )}
    </div>
  );
}
